import {
  addDays,
  OkResponse,
  PatternResponse,
  reanchorWeekly,
  resolveDay,
  SplitList,
  SplitResponse,
  startOfWeek,
  SUGGESTED_SPLITS,
  todayIn,
  ExerciseList,
  type MuscleGroup,
  type Split,
  type Units,
  type User,
} from '@squats/shared';
import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { Link } from 'wouter';
import { api, errorText } from '../api.ts';
import { useUser } from '../auth.tsx';
import { useCachedApi } from '../cache.ts';
import { forUser, isPlanner, splitTone } from '../calendar.tsx';
import { MuscleChips, MusclePicker } from '../components/Muscles.tsx';
import { PersonPicker, usePeople } from '../components/People.tsx';
import { PlanEditor } from '../components/Plan.tsx';
import { Sheet } from '../components/Sheet.tsx';
import { formatDayShort, formatWeekday } from '../dates.ts';
import { useApi } from '../useApi.ts';

/** Someone's splits (each with its exercise template) and repeating schedule. The planner's page, for anyone. */
export function PlanSetupPage() {
  const user = useUser();
  const people = usePeople(user);
  const [who, setWho] = useState(user.id);
  const path = (p: string) => forUser(p, who, user);
  const { data, error, reload } = useApi(() => api(path('/api/splits'), SplitList), [who]);
  const splits = data?.splits ?? [];
  const units = people.find((p) => p.id === who)?.units ?? user.units;
  if (!isPlanner(user)) {
    return (
      <div className="stack">
        <Link href="/plan" className="small">
          ‹ Plan
        </Link>
        <p>Your splits and schedule are set up by the planner.</p>
      </div>
    );
  }
  return (
    <div className="stack-lg">
      <div className="stack-sm">
        <Link href="/plan" className="small">
          ‹ Plan
        </Link>
        <div className="row wrap">
          <h1 className="grow">Splits & schedule</h1>
          <PersonPicker user={user} people={people} value={who} onChange={setWho} label="Whose plan" />
        </div>
      </div>
      {error && <p className="error">{error}</p>}
      <SplitsSection key={`splits-${who}`} user={user} path={path} units={units} splits={splits} onChanged={reload} loaded={data !== undefined} />
      <PatternSection key={`pattern-${who}`} path={path} splits={splits} />
    </div>
  );
}

function SplitsSection({
  user,
  path,
  units,
  splits,
  onChanged,
  loaded,
}: {
  user: User;
  /** Adds `?user=` when planning for someone else. */
  path: (p: string) => string;
  /** The person's units, for exercises added to a template. */
  units: Units;
  splits: Split[];
  onChanged: () => void;
  loaded: boolean;
}) {
  const [editing, setEditing] = useState<string | 'new' | null>(null);
  const [templateFor, setTemplateFor] = useState<Split | null>(null);
  const { data: library } = useCachedApi(user.id, '/api/exercises', ExerciseList);
  const exercises = useMemo(() => new Map((library?.exercises ?? []).map((e) => [e.id, e])), [library]);
  const [error, setError] = useState<string | null>(null);
  const suggestions = SUGGESTED_SPLITS.filter((s) => !splits.some((x) => x.name.toLowerCase() === s.name.toLowerCase()));

  async function addSuggestion(s: (typeof SUGGESTED_SPLITS)[number]) {
    setError(null);
    try {
      await api(path('/api/splits'), SplitResponse, { body: s });
      onChanged();
    } catch (e) {
      setError(errorText(e));
    }
  }

  async function remove(s: Split) {
    if (!confirm(`Remove “${s.name}”? Days that already used it keep its name.`)) return;
    setError(null);
    try {
      await api(path(`/api/splits/${s.id}`), OkResponse, { method: 'DELETE' });
      onChanged();
    } catch (e) {
      setError(errorText(e));
    }
  }

  return (
    <section className="card stack" aria-labelledby="splits-h">
      <h2 id="splits-h">Splits</h2>
      <p className="muted small">The muscle groups trained together on one day, and the exercises each day of it plans (its template).</p>
      {loaded && splits.length === 0 && <p>No splits yet. Tap a suggestion below, or make your own.</p>}
      <ul className="list">
        {splits.map((s) =>
          editing === s.id ? (
            <li key={s.id} className="list-row">
              <SplitForm path={path} initial={s} onDone={() => { setEditing(null); onChanged(); }} onCancel={() => setEditing(null)} />
            </li>
          ) : (
            <li key={s.id} className="list-row">
              <div className="grow stack-sm">
                <span className={`plan tone-${splitTone(s.id)}`}>{s.name}</span>
                <MuscleChips groups={s.muscleGroups} muted />
                {s.exercises.length ? (
                  <span className="muted small">{s.exercises.map((e) => exercises.get(e.exerciseId)?.name ?? 'Exercise').join(' · ')}</span>
                ) : (
                  <span className="muted small">No exercises planned yet.</span>
                )}
              </div>
              <div className="row">
                <button type="button" className="btn small primary" onClick={() => setTemplateFor(s)}>
                  Exercises
                </button>
                <button type="button" className="btn small" onClick={() => setEditing(s.id)}>
                  Edit
                </button>
                <button type="button" className="btn small danger" onClick={() => void remove(s)}>
                  Remove
                </button>
              </div>
            </li>
          ),
        )}
      </ul>
      {error && (
        <p className="error" role="alert">
          {error}
        </p>
      )}
      {editing === 'new' ? (
        <SplitForm path={path} onDone={() => { setEditing(null); onChanged(); }} onCancel={() => setEditing(null)} />
      ) : (
        <div>
          <button type="button" className="btn" onClick={() => setEditing('new')}>
            New split
          </button>
        </div>
      )}
      <Sheet open={templateFor !== null} onClose={() => setTemplateFor(null)} title={`${templateFor?.name ?? 'Split'}: exercises`}>
        {templateFor && (
          <PlanEditor
            mode="template"
            initial={templateFor.exercises}
            exercises={exercises}
            groups={templateFor.muscleGroups}
            units={units}
            onCancel={() => setTemplateFor(null)}
            onSave={async (entries) => {
              await api(path(`/api/splits/${templateFor.id}/exercises`), SplitResponse, { method: 'PUT', body: entries });
              setTemplateFor(null);
              onChanged();
            }}
          />
        )}
      </Sheet>
      {suggestions.length > 0 && (
        <div className="stack-sm">
          <span className="muted small">Quick add</span>
          <div className="chips">
            {suggestions.map((s) => (
              <button key={s.name} type="button" className="chip toggle" onClick={() => void addSuggestion(s)}>
                + {s.name}
              </button>
            ))}
          </div>
        </div>
      )}
    </section>
  );
}

function SplitForm({ path, initial, onDone, onCancel }: { path: (p: string) => string; initial?: Split; onDone: () => void; onCancel: () => void }) {
  const [name, setName] = useState(initial?.name ?? '');
  const [groups, setGroups] = useState<MuscleGroup[]>(initial?.muscleGroups ?? []);
  const [error, setError] = useState<string | null>(null);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    try {
      const body = { name, muscleGroups: groups };
      if (initial) await api(path(`/api/splits/${initial.id}`), SplitResponse, { method: 'PATCH', body });
      else await api(path('/api/splits'), SplitResponse, { body });
      onDone();
    } catch (err) {
      setError(errorText(err));
    }
  }

  return (
    <form className="stack grow" onSubmit={submit}>
      <label className="field">
        <span>Name</span>
        <input value={name} onChange={(e) => setName(e.target.value)} maxLength={40} required placeholder="e.g. Chest & Triceps" autoFocus />
      </label>
      <MusclePicker label="Muscle groups" value={groups} onChange={setGroups} />
      {error && <p className="error">{error}</p>}
      <div className="row">
        <button type="submit" className="btn primary" disabled={!name.trim() || groups.length === 0}>
          {initial ? 'Save' : 'Add split'}
        </button>
        <button type="button" className="btn" onClick={onCancel}>
          Cancel
        </button>
      </div>
    </form>
  );
}

function PatternSection({ path, splits }: { path: (p: string) => string; splits: Split[] }) {
  const user = useUser();
  const today = todayIn(user.timezone);
  const { data, reload } = useApi(() => api(path('/api/pattern'), PatternResponse), []);
  const [anchorDate, setAnchorDate] = useState(startOfWeek(today));
  const [slots, setSlots] = useState<(string | null)[]>(Array(7).fill(null));
  const [effectiveFrom, setEffectiveFrom] = useState(today);
  const [status, setStatus] = useState<{ kind: 'saved' } | { kind: 'error'; message: string } | null>(null);

  // Start from the saved pattern, if there is one. Weekly ones are shown from
  // Sunday, whatever date they were anchored on.
  useEffect(() => {
    const p = data?.pattern;
    if (!p) return;
    const anchor = p.slots.length === 7 ? startOfWeek(p.anchorDate) : p.anchorDate;
    setAnchorDate(anchor);
    setSlots(reanchorWeekly(p.anchorDate, p.slots, anchor));
  }, [data]);

  const weekly = slots.length === 7;
  // For a weekly plan the start date only decides which day is listed first;
  // each weekday keeps its split.
  const moveAnchor = (next: string) => {
    setSlots((prev) => reanchorWeekly(anchorDate, prev, next));
    setAnchorDate(next);
  };
  const resize = (n: number) => setSlots((prev) => Array.from({ length: n }, (_, i) => prev[i] ?? null));
  const draft = { id: 'draft', anchorDate, effectiveFrom, slots };
  const preview = Array.from({ length: 14 }, (_, i) => resolveDay([draft], undefined, addDays(effectiveFrom, i)));
  const nameOf = (id: string | null) => splits.find((s) => s.id === id)?.name;

  async function save(e: FormEvent) {
    e.preventDefault();
    setStatus(null);
    try {
      await api(path('/api/pattern'), PatternResponse, { method: 'PUT', body: { anchorDate, effectiveFrom, slots } });
      setStatus({ kind: 'saved' });
      reload();
    } catch (err) {
      setStatus({ kind: 'error', message: errorText(err) });
    }
  }

  return (
    <form className="card stack" onSubmit={save} aria-labelledby="pattern-h">
      <h2 id="pattern-h">Repeating schedule</h2>
      <p className="muted small">
        A cycle that repeats: 7 days for the same plan every week, or another length for a rotation (e.g. 4 days: Push, Pull, Legs, Rest). You can
        still change any single day on the Plan screen.
      </p>
      <div className="row wrap">
        <label className="field">
          <span>Cycle length</span>
          <select value={slots.length} onChange={(e) => resize(Number(e.target.value))}>
            {Array.from({ length: 14 }, (_, i) => i + 1).map((n) => (
              <option key={n} value={n}>
                {n} day{n > 1 ? 's' : ''}
                {n === 7 ? ' (weekly)' : ''}
              </option>
            ))}
          </select>
        </label>
        <label className="field">
          <span>{weekly ? 'Week starts on' : 'Day 1 is'}</span>
          <input type="date" value={anchorDate} onChange={(e) => e.target.value && moveAnchor(e.target.value)} required />
        </label>
      </div>

      <ol className="slots">
        {slots.map((slot, i) => (
          <li key={i} className="slot">
            <span className="slot-label">{weekly ? formatWeekday(addDays(anchorDate, i), true) : `Day ${i + 1}`}</span>
            <select
              aria-label={`${weekly ? formatWeekday(addDays(anchorDate, i), true) : `Day ${i + 1}`} plan`}
              value={slot ?? ''}
              onChange={(e) => setSlots(slots.map((s, j) => (j === i ? e.target.value || null : s)))}
            >
              <option value="">Rest</option>
              {splits.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
              {slot && !nameOf(slot) && <option value={slot}>(removed split)</option>}
            </select>
          </li>
        ))}
      </ol>

      <label className="field">
        <span>Use this schedule from</span>
        <input type="date" value={effectiveFrom} onChange={(e) => e.target.value && setEffectiveFrom(e.target.value)} required />
      </label>

      <details>
        <summary>Preview the next two weeks</summary>
        <ul className="list preview">
          {preview.map((d) => (
            <li key={d.date} className="list-row compact">
              <span className="day-name">{formatDayShort(d.date)}</span>
              <span className={d.splitId ? `plan tone-${splitTone(d.splitId)}` : 'plan plain rest'}>{nameOf(d.splitId) ?? 'Rest'}</span>
            </li>
          ))}
        </ul>
      </details>

      <div className="row">
        <button type="submit" className="btn primary" disabled={!slots.some(Boolean)}>
          Save schedule
        </button>
        {status?.kind === 'saved' && <span className="muted small">Saved</span>}
        {status?.kind === 'error' && <span className="error">{status.message}</span>}
      </div>
      {splits.length === 0 && <p className="muted small">Add a split above first.</p>}
    </form>
  );
}
